import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2, RotateCcw, Clock, User } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalInboxPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [actionModal, setActionModal] = useState(null);
  const [comments, setComments] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => { fetchApprovals(); }, [statusFilter]);

  const fetchApprovals = async () => {
    try {
      const params = { limit: 50 };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/approvals", { params });
      setApprovals(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { setApprovals([]); }
  };

  const handleAction = async (approvalId, action) => {
    if (!navigator.onLine) {
      toast.error("Approval requires an active connection. Connect to the network first.");
      return;
    }
    setActing(true);
    try {
      await api.post(`/approvals/${approvalId}/${action}`, { comments });
      toast.success(`Approval ${action === "approve" ? "approved" : "sent for revision"}`);
      setActionModal(null);
      setComments("");
      fetchApprovals();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
    setActing(false);
  };

  const statusBadge = (status) => {
    const map = {
      PENDING: { cls: "bg-yellow-100 text-yellow-800", icon: Clock },
      IN_PROGRESS: { cls: "bg-blue-100 text-blue-800", icon: Clock },
      APPROVED: { cls: "bg-green-100 text-green-800", icon: CheckCircle2 },
      REVISED: { cls: "bg-orange-100 text-orange-800", icon: RotateCcw },
      REJECTED: { cls: "bg-red-100 text-red-800", icon: null },
    };
    const m = map[status] || { cls: "bg-gray-100 text-gray-700" };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${m.cls}`}>{status}</span>;
  };

  return (
    <div data-testid="approval-inbox-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>Approval Inbox</h1>
          <p className="text-sm text-gray-500 mt-1">{total} pending approvals</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REVISED">Revised</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {approvals.length === 0 && (
          <Card className="border border-gray-200 rounded-sm"><CardContent className="p-8 text-center text-gray-400 text-sm">No approvals found</CardContent></Card>
        )}
        {approvals.map((a) => (
          <Card key={a.id} data-testid={`approval-item-${a.id}`} className="border border-gray-200 shadow-sm rounded-sm hover:shadow transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-semibold text-[#09090B]">{a.entity_number || `${a.entity_type}-${a.entity_id?.slice(0, 8)}`}</span>
                    {statusBadge(a.status)}
                    <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-gray-100 uppercase">{a.entity_type}</span>
                  </div>
                  {a.entity_data && (
                    <div className="text-sm text-gray-600 space-x-4">
                      {a.entity_data.customer_name && <span>Customer: {a.entity_data.customer_name}</span>}
                      {a.entity_data.total_amount != null && <span>Amount: {a.entity_data.total_amount.toFixed(2)}</span>}
                      {a.entity_data.type && <span>Type: {a.entity_data.type}</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <User size={12} /> Submitted by {a.creator_name}
                    <span className="ml-2">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                  </div>
                  {a.steps?.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {a.steps.map((step) => (
                        <div key={step.id} className={`text-xs px-2 py-1 rounded-sm border ${step.status === "APPROVED" ? "border-green-300 bg-green-50" : step.status === "PENDING" ? "border-gray-300 bg-gray-50" : "border-orange-300 bg-orange-50"}`}>
                          Step {step.step_order}: {step.role_required} — {step.status}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {(a.status === "PENDING" || a.status === "IN_PROGRESS") && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      data-testid={`approve-btn-${a.id}`}
                      size="sm"
                      onClick={() => setActionModal({ id: a.id, action: "approve" })}
                      className="bg-green-600 hover:bg-green-700 rounded-sm"
                    >
                      <CheckCircle2 size={14} className="mr-1" /> Approve
                    </Button>
                    <Button
                      data-testid={`revise-btn-${a.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => setActionModal({ id: a.id, action: "revise" })}
                      className="rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      <RotateCcw size={14} className="mr-1" /> Revise
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!actionModal} onOpenChange={() => setActionModal(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
              {actionModal?.action === "approve" ? "Approve" : "Request Revision"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea data-testid="approval-comments" value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Add comments (optional)..." className="rounded-sm" rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModal(null)} className="rounded-sm">Cancel</Button>
            <Button
              data-testid="confirm-approval-action"
              onClick={() => handleAction(actionModal.id, actionModal.action)}
              disabled={acting}
              className={`rounded-sm ${actionModal?.action === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-orange-600 hover:bg-orange-700"}`}
            >
              {acting ? "Processing..." : actionModal?.action === "approve" ? "Confirm Approve" : "Send for Revision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
